import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarPlusIcon, CheckIcon } from 'lucide-react';
import { useStudio } from '../../contexts/StudioContext';
import { site } from '../../data/site';
import { vehicleSizes } from '../../data/schedule';
import { Booking } from '../../types/booking';
import { duration, euro, icsFile, longDate, timeRange } from '../../utils/format';
type Props = {
  booking: Booking;
  onReset: () => void;
};
export function Confirmation({
  booking,
  onReset
}: Props) {
  const {
    services,
    extras
  } = useStudio();
  const service = services.find(s => s.id === booking.serviceId);
  const chosen = extras.filter(e => booking.extraIds.includes(e.id));
  const size = vehicleSizes.find(v => v.id === booking.vehicleSizeId);
  const ics = icsFile({
    reference: booking.reference,
    title: `Daily Car Care — ${service?.name ?? 'Afspraak'}`,
    date: booking.date,
    time: booking.time,
    minutes: booking.durationMinutes,
    location: site.address,
    description: `Referentie ${booking.reference}. Totaal ${euro(booking.amount)}.`
  });
  return <div className="mx-auto max-w-2xl pb-24 pt-24 sm:pt-32 lg:pb-32 lg:pt-40" data-id="element-356" data-mp="qnhl0jgqhe3v">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-ember text-void glow" data-id="element-357" data-mp="qlekh5hyg2kw">
        <CheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-358" data-mp="gp1v2jtg0avy" />
      </span>
      <h1 className="mt-8 font-display text-3xl font-extrabold leading-[1.05] tracking-tightest text-chalk sm:text-4xl" data-id="element-359" data-mp="gqv0xww8er2w">
        {booking.status === 'confirmed' ? 'Uw afspraak staat vast' : 'Uw aanvraag is ontvangen'}
      </h1>
      <p className="mt-5 text-base leading-relaxed text-dim" data-id="element-360" data-mp="14emcgy9y7034">
        Bedankt {booking.customer.name.split(' ')[0]}. De bevestiging is onderweg naar {booking.customer.email}{' '}
        onder referentie <span className="font-medium text-ember" data-id="element-361" data-mp="3p4va0u84leaw">{booking.reference}</span>.
        {booking.status === 'confirmed' ? ' Uw aanbetaling is ontvangen en het tijdslot is voor u gereserveerd.' : ' Wij bellen u binnen 24 uur om de afspraak definitief te bevestigen.'}
      </p>

      <dl className="mt-10 divide-y divide-edge overflow-hidden rounded-2xl border border-edge bg-carbon text-sm" data-id="element-362" data-mp="3mikvxmv0rslm">
        <div className="flex justify-between gap-6 px-6 py-4" data-id="element-363" data-mp="3cmhrubglxjw1">
          <dt className="text-dim" data-id="element-364" data-mp="3clzkojsepl9c">Behandeling</dt>
          <dd className="text-right font-medium text-chalk" data-id="element-365" data-mp="3hkjc2zprrp93">{service?.name}</dd>
        </div>
        <div className="flex justify-between gap-6 px-6 py-4" data-id="element-366" data-mp="33te67fvt60qb">
          <dt className="text-dim" data-id="element-367" data-mp="33s33zki0h11b">Voertuig</dt>
          <dd className="text-right font-medium text-chalk" data-id="element-368" data-mp="38kwc8icouri5">
            {booking.customer.car} · {size?.label}
          </dd>
        </div>
        {chosen.length > 0 && <div className="flex justify-between gap-6 px-6 py-4" data-id="element-369" data-mp="2yul38he5ds4e">
            <dt className="text-dim" data-id="element-370" data-mp="2ysqhx1yyrxzs">Extra's</dt>
            <dd className="text-right font-medium text-chalk" data-id="element-371" data-mp="2kutizyx1ffhy">{chosen.map(e => e.name).join(', ')}</dd>
          </div>}
        <div className="flex justify-between gap-6 px-6 py-4" data-id="element-372" data-mp="2ow5c4ef4eiuw">
          <dt className="text-dim" data-id="element-373" data-mp="267uv2dxe337z">Wanneer</dt>
          <dd className="text-right font-medium capitalize text-chalk" data-id="element-374" data-mp="268mphlf92uwo">
            {longDate(booking.date)} · {timeRange(booking.time, booking.durationMinutes)}
          </dd>
        </div>
        <div className="flex justify-between gap-6 px-6 py-4" data-id="element-375" data-mp="2fam7afy3a30o">
          <dt className="text-dim" data-id="element-376" data-mp="2a9osmdcafr3x">Duur</dt>
          <dd className="text-right font-medium text-chalk" data-id="element-377" data-mp="2a8n5hahzze5u">{duration(booking.durationMinutes)}</dd>
        </div>
        <div className="flex justify-between gap-6 px-6 py-4" data-id="element-378" data-mp="2p54hm3y0gwtg">
          <dt className="text-dim" data-id="element-379" data-mp="2k7cpp9xd9ap9">Waar</dt>
          <dd className="text-right font-medium text-chalk" data-id="element-380" data-mp="2k6b8soto5tzm">{site.address}</dd>
        </div>
        <div className="flex items-center justify-between gap-6 px-6 py-4" data-id="element-381" data-mp="2y1df3d2fhlcy">
          <dt className="text-dim" data-id="element-382" data-mp="2tyqu9w46d0p9">Betaling</dt>
          <dd className="text-right font-medium text-chalk" data-id="element-383" data-mp="37xgon3zm0m9x">
            {booking.paymentMethod === 'deposit' ? `${euro(booking.depositAmount)} aanbetaald · rest ${euro(booking.amount - booking.depositAmount)}` : 'In de werkplaats na de behandeling'}
          </dd>
        </div>
        <div className="flex items-center justify-between gap-6 bg-panel px-6 py-5" data-id="element-384" data-mp="3r1tldct8lxy8">
          <dt className="text-dim" data-id="element-385" data-mp="3vtbr0e67gmpj">Totaal</dt>
          <dd className="font-display text-2xl font-extrabold tracking-tight text-ember" data-id="element-386" data-mp="3vtlhe2v5wvvr">
            {euro(booking.amount)}
          </dd>
        </div>
      </dl>

      <div className="mt-9 flex flex-wrap gap-3" data-id="element-387" data-mp="342dvz3gd8q0w">
        <a href={ics} download={`${booking.reference}.ics`} className="inline-flex items-center gap-2 rounded-full bg-ember px-6 py-3.5 text-sm font-semibold text-void transition-[background-color,box-shadow] duration-200 ease-smooth hover:bg-white hover:glow" data-id="element-388" data-mp="343p4thoo4nyv">
          <CalendarPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-389" data-mp="3hzkdyvt9mkp2" />
          In mijn agenda zetten
        </a>
        <Link to={`/appointment?ref=${booking.reference}`} className="rounded-full border border-chalk/20 px-6 py-3.5 text-sm font-medium text-chalk transition-colors duration-200 ease-smooth hover:border-ember hover:text-ember" data-id="element-390" data-mp="sepf5pipzuax">
          Afspraak beheren
        </Link>
        <button type="button" onClick={onReset} className="rounded-full px-6 py-3.5 text-sm font-medium text-dim transition-colors duration-200 ease-smooth hover:text-chalk" data-id="element-391" data-mp="2cwq9vyytjl91">
          Nog een afspraak
        </button>
      </div>
    </div>;
}